/**
 * SetupStep — choose what to import, pick the CSV file, and (for deals)
 * the target pipeline + fallback stage.
 *
 * Parsing happens here with papaparse (header row, quoted fields,
 * embedded commas/newlines all handled — unlike the naive splitter this
 * wizard replaced). The parsed rows and headers are lifted to the
 * wizard; nothing is uploaded yet.
 */

import { useRef } from 'react';
import Papa from 'papaparse';
import { Building2, Contact, Handshake, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFileDropZone } from '@/lib/hooks/useFileDropZone';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FiasLeadsPipelineDto } from '@/lib/api';
import {
  MAX_IMPORT_FILE_BYTES,
  MAX_IMPORT_ROWS,
  type ImportEntityType,
} from '../../lib/csv-mapping';

const ENTITY_CARDS: Array<{
  type: ImportEntityType;
  label: string;
  description: string;
  icon: typeof Contact;
}> = [
  {
    type: 'contact',
    label: 'Contacts',
    description: 'People — names, emails, phones, job titles',
    icon: Contact,
  },
  {
    type: 'company',
    label: 'Companies',
    description: 'Organizations — names, domains, industries',
    icon: Building2,
  },
  {
    type: 'deal',
    label: 'Deals',
    description: 'Sales opportunities in one of your pipelines',
    icon: Handshake,
  },
];

interface Props {
  entityType: ImportEntityType;
  fileName: string | null;
  rowCount: number;
  parseError: string | null;
  pipelines: FiasLeadsPipelineDto[];
  pipelineId: string | null;
  defaultStageId: string | null;
  onEntityTypeChange: (type: ImportEntityType) => void;
  onFileParsed: (fileName: string, headers: string[], rows: Array<Record<string, string>>) => void;
  onParseError: (message: string) => void;
  onPipelineChange: (pipelineId: string | null) => void;
  onDefaultStageChange: (stageId: string | null) => void;
}

export function SetupStep({
  entityType,
  fileName,
  rowCount,
  parseError,
  pipelines,
  pipelineId,
  defaultStageId,
  onEntityTypeChange,
  onFileParsed,
  onParseError,
  onPipelineChange,
  onDefaultStageChange,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      onParseError('File is larger than 10 MB. Split it into smaller files and import each one.');
      return;
    }
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (result) => {
        // 'Delimiter' (single-column files) and 'FieldMismatch' (ragged
        // rows) are benign — the rows still parse, and the preview step
        // surfaces any real problems per cell. Only structural failures
        // (e.g. broken quoting) block here.
        const fatal = result.errors.find(
          (e) => e.type !== 'Delimiter' && e.type !== 'FieldMismatch',
        );
        if (fatal) {
          onParseError(`Could not read the CSV: ${fatal.message}`);
          return;
        }
        const headers = result.meta.fields ?? [];
        if (headers.length === 0 || result.data.length === 0) {
          onParseError('The file has no data rows. It needs a header row plus at least one row.');
          return;
        }
        if (result.data.length > MAX_IMPORT_ROWS) {
          onParseError(
            `The file has ${result.data.length.toLocaleString()} rows — the limit is ${MAX_IMPORT_ROWS.toLocaleString()} per import. Split it and import each part.`,
          );
          return;
        }
        onFileParsed(file.name, headers, result.data);
      },
      error: (err) => onParseError(err.message),
    });
  };

  const { isDragActive, dropZoneProps } = useFileDropZone({
    onDrop: (files) => {
      if (files[0]) handleFile(files[0]);
    },
  });

  const selectedPipeline = pipelines.find((p) => p.id === pipelineId) ?? null;

  return (
    <div className="flex flex-col gap-5">
      <section>
        <h3 className="text-sm font-medium mb-2">What are you importing?</h3>
        <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Import type">
          {ENTITY_CARDS.map((card) => {
            const Icon = card.icon;
            const selected = entityType === card.type;
            return (
              <button
                key={card.type}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onEntityTypeChange(card.type)}
                className={cn(
                  'rounded-lg border p-3 text-left transition-colors',
                  selected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border bg-card hover:border-primary/40',
                )}
              >
                <div className="flex items-center gap-2 font-medium text-sm">
                  <Icon className="h-4 w-4" />
                  {card.label}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{card.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      {entityType === 'deal' && (
        <section className="rounded-lg border border-border bg-card p-3 flex flex-col gap-3">
          <div>
            <h3 className="text-sm font-medium">Where should the deals go?</h3>
            <p className="text-xs text-muted-foreground">
              Rows whose Stage column matches a stage name land there; everything else lands in the
              default stage you pick here.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground text-xs">Pipeline</span>
              <Select value={pipelineId ?? undefined} onValueChange={(v) => onPipelineChange(v)}>
                <SelectTrigger aria-label="Pipeline">
                  <SelectValue placeholder="Choose a pipeline" />
                </SelectTrigger>
                <SelectContent>
                  {pipelines.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground text-xs">Default stage</span>
              <Select
                value={defaultStageId ?? undefined}
                onValueChange={(v) => onDefaultStageChange(v)}
                disabled={!selectedPipeline}
              >
                <SelectTrigger aria-label="Default stage">
                  <SelectValue
                    placeholder={selectedPipeline ? 'Choose a stage' : 'Pick a pipeline first'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(selectedPipeline?.stages ?? []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>
        </section>
      )}

      <section>
        <h3 className="text-sm font-medium mb-2">Your CSV file</h3>
        <div
          {...dropZoneProps}
          className={cn(
            'relative rounded-lg border-2 border-dashed p-6 text-center transition-colors',
            isDragActive ? 'border-primary bg-primary/5' : 'border-border bg-card',
          )}
        >
          <Upload className="mx-auto h-6 w-6 text-muted-foreground" />
          <p className="mt-2 text-sm">
            Drag a CSV here, or{' '}
            <button
              type="button"
              className="text-primary underline"
              onClick={() => fileInputRef.current?.click()}
            >
              choose a file
            </button>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Any column names work — you'll match them to Fias Leads fields in the next step.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            aria-label="CSV file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
          />
        </div>
        {fileName && !parseError && (
          <p className="mt-2 text-sm">
            <span className="font-medium">{fileName}</span>{' '}
            <span className="text-muted-foreground">
              — {rowCount.toLocaleString()} row{rowCount === 1 ? '' : 's'} found
            </span>
          </p>
        )}
        {parseError && <p className="mt-2 text-sm text-destructive">{parseError}</p>}
      </section>
    </div>
  );
}
